import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { ApiErrorMessage, apiFoutmelding } from '@/components/feedback/ApiErrorMessage';
import { FilterRejectedMessage } from '@/components/feedback/FilterRejectedMessage';
import { DatumFilter, useStartadres, type StartadresLijst } from '@/components/tabel';
import { useLijstStandStore } from '@/stores/lijstStandStore';
import { datumOmgedraaid, type Datumbereik, type FilterStand, type Kolom } from '@xxtract/tabelfilters';
import { useEffect, useRef, useState } from 'react';
import { useHandlingTimePerPerson } from '../hooks/useHandlingTimePerPerson';
import { useHandlingTimeNormen, useZetHandlingTimeNormen } from '../hooks/useHandlingTimeNormen';
import { useHandlingTimeWorkWindow } from '../hooks/useHandlingTimeWorkWindow';
import { useEffectiveAccess } from '@/lib/effectiveAccess';
import { samenvatPerPersoon, type WerkvensterWeergave } from '../werkvenster';
import { Contextkaarten } from '../components/Contextkaarten';
import { BehandeltijdGrafiek } from '../components/BehandeltijdGrafiek';
import { BEHANDELTIJD_TABEL_SLEUTEL, BehandeltijdTabel } from '../components/BehandeltijdTabel';
import { Aandachtslijst } from '../components/Aandachtslijst';
import { CategorieBlok } from '../components/CategorieBlok';
import { PerEtiketOverzicht } from '../components/PerEtiketOverzicht';
import { berekenKerncijfers } from '../kerncijfers';
import { BEHANDELTIJD_LIJST, bouwTabelRegels, leesTabelOpen, TABEL_OPEN, TABEL_PARAMETER } from '../tabel';
import { BEHANDELTIJD_PAGINAFILTER, leesPaginafilter } from '../paginafilter';
import { bouwDekking, bouwPersoonsverdeling } from '../contextkaarten';
import { bouwReeks, korrelVoor } from '../reeks';
import { naamVoorRegel } from '../naam';
import { PRESETS, periodeVoor, presetVoor, STANDAARD_PRESET, type PeriodePreset } from '../periodePresets';
import { vorigePeriode } from '../vorigePeriode';
import { isGeldigGln, isHandlingTimeGateClosed } from '../handlingTime.api';
import { voorlopigTot, voorlopigZichtbaar } from '../amsterdamTijd';
import type { Normen } from '../normen';
import {
  beperkBucketsTotStap,
  beperkRijenTotStap,
  STAPFILTERS,
  uitsplitsingOntbreekt,
  type StapFilter,
  type StapKeuze,
} from '../stapFilter';

/**
 * Story 28-2 — de behandeltijd per persoon, sinds epic 31 met een kop erboven.
 *
 * Het scherm begon bewust klein: één tabel, geen grafiek, geen uitvoerknop. Story 31.2 zette daar
 * de kerncijfers boven en story 31.3 de grafiek over de tijd ertussen; de **uitvoerknop** blijft
 * er met opzet af, en de periode wordt nog steeds gekozen zoals dit portaal dat al doet — twee
 * kale datumvelden, net als de analysepagina van de orders — zodat er geen nieuw onderdeel
 * bijkomt voor iets wat er al is.
 *
 * De volgorde ligt vast en is geen opmaakkeuze, en sinds epic 33 is het een ANDERE volgorde: eerst
 * waar op te sturen valt (de aandachtslijst), dan het verloop (de grafiek), dan wie (de ingeklapte
 * tabel), en pas onderaan hoe hard de cijfers zijn (de drie contextkaarten). Tot story 33.3 stonden
 * er vier grote getallen bovenaan, en die trokken de eerste blik weg van wat er te sturen valt.
 * Elk blok komt uit een eigen vraag, zodat er geen één het scherm kan meetrekken als hij mislukt.
 *
 * Het scherm toont wat de dienst zegt en niets meer. Het zoekt geen namen op, vult niets aan en
 * maakt van een ontbrekende naam geen eigen "onbekende gebruiker": de drie uitkomsten zonder naam
 * houden elk hun eigen tekst. Zou het portaal ze samenvoegen, dan wordt een geanonimiseerde meting
 * ononderscheidbaar van een systeemmelding.
 *
 * Sinds story 31.5 staat er één instelling op dit scherm: de twee normen in minuten per stuk. Ze
 * worden HIER gezet en niet in het beheerscherm, want dat staat achter `admin` en eist een reden —
 * samen zou dat precies de leidinggevende buitensluiten die de norm hoort te zetten. Eén norm werkt
 * op alle drie de plekken tegelijk door: de aandachtslijst, de gestippelde lijn in de grafiek, en
 * de kleur in de tabel.
 */

/**
 * De verwijzing tussen de opening en de tabel eronder (story 33.2).
 *
 * Als constante en niet als losse tekst op twee plekken: raken die twee uit elkaar, dan wijst de
 * opening naar niets en weet een schermlezer niet wat hij open- of dichtklapt.
 */
const TABEL_INHOUD_ID = 'handling-time-tabel-inhoud';

/** De lijststand van periode en stap (spec-tabelfilters §7); de tabel eronder heeft een eigen stand. */
const PAGINAFILTER_SLEUTEL = 'behandeltijd';

/**
 * Het GLN dat het scherm kiest zolang het adres er geen noemt (behandeltijd-rapport, CAP-3).
 * Een lege `gln` in het adres betekent juist alle GLN's; dat is een andere keuze.
 */
const STANDAARD_GLN = '8719331250108';

/** Eén lezer voor de binnenkomende link: periode en stap naar de paginafilter, `naam`/`sort` naar de tabel. */
const STARTLIJSTEN: readonly StartadresLijst[] = [
  { lijst: BEHANDELTIJD_PAGINAFILTER, standSleutel: PAGINAFILTER_SLEUTEL },
  { lijst: BEHANDELTIJD_LIJST, standSleutel: BEHANDELTIJD_TABEL_SLEUTEL },
];

/** Testid-voorvoegsel van de melding bij een ongeldige link (`handling-time-filter-rejected`). */
const BASIS_TESTID = 'handling-time';

const PERIODE_KOLOM = BEHANDELTIJD_PAGINAFILTER.kolommen.find((kolom) => kolom.sleutel === 'periode') as Kolom<never, never>;

/** De twee stappen in hun vaste volgorde: vastleggen vóór valideren. */
const NORM_STAPPEN = ['vastleggen', 'valideren'] as const;

/** De twee invulvelden, als tekst: precies wat er staat, ook zolang het nog geen getal is. */
interface Normvelden {
  vastleggen: string;
  valideren: string;
}

/** Een bewaarde norm zoals hij in het invulveld staat. Geen norm is een LEEG veld, nooit een nul. */
function alsVeld(norm: number | null | undefined): string {
  return norm === null || norm === undefined ? '' : String(norm);
}

/**
 * Wat er in een veld staat, terug naar een norm.
 *
 * Leeg betekent "geen norm" en dat is iets anders dan nul. De komma wordt aanvaard omdat mensen die
 * nu eenmaal typen; alles wat daarna nog geen getal boven nul is wordt hier tegengehouden, zodat
 * `NaN` niet als `null` de deur uit gaat en stilzwijgend een norm zou wissen.
 */
function alsNorm(veld: string): { ok: true; norm: number | null } | { ok: false } {
  const schoon = veld.trim().replace(',', '.');
  if (schoon === '') return { ok: true, norm: null };
  const getal = Number(schoon);
  if (!Number.isFinite(getal) || getal <= 0) return { ok: false };
  return { ok: true, norm: getal };
}

export function BehandeltijdPerPersoon() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const kopRef = useRef<HTMLHeadingElement>(null);

  /**
   * spec-tabelfilters story 6 — periode en stap staan in de lijststand, niet meer in het adres.
   *
   * Een oude link met `from`, `to` en `stap` opent precies zo, waarna die parameters uit het adres
   * gaan; een ongeldige waarde wordt gemeld (CAP-14). Zonder stand: de laatste dertig dagen en
   * vastleggen, zonder dat er iets in het adres of de stand wordt gezet. Alleen een ONTBREKENDE
   * periode is "standaard": wie een datumveld leegmaakt om een nieuwe te typen, krijgt de standaard
   * er niet overheen.
   */
  const { start, geweigerd, wisGeweigerd } = useStartadres(STARTLIJSTEN);
  const bewaardePaginafilter = useLijstStandStore((stand) => stand.standen[PAGINAFILTER_SLEUTEL]?.filters);
  const zetPaginafilter = useLijstStandStore((stand) => stand.zetPaginafilter);
  const paginafilters: FilterStand | undefined = start[PAGINAFILTER_SLEUTEL]?.filters ?? bewaardePaginafilter;
  // Eén keer per keer openen: anders schuift de standaardperiode stil op zodra het middernacht wordt.
  const [standaardPeriode] = useState(() => periodeVoor(STANDAARD_PRESET, new Date()));
  const { from: gekozenVan, to: gekozenTot, stap: gevraagdFilter } = leesPaginafilter(paginafilters, standaardPeriode);
  /** Wat in de datumvelden staat; ook een omgedraaid bereik blijft daar staan zodat het te herstellen is. */
  const gekozenBereik: Datumbereik = { ...(gekozenVan ? { van: gekozenVan } : {}), ...(gekozenTot ? { tot: gekozenTot } : {}) };
  // Een omgedraaid bereik ("Vanaf" na "Tot en met") is niet toegepast (CAP-3): geen periode, dus geen vraag.
  const omgedraaid = datumOmgedraaid(gekozenBereik);
  const from = omgedraaid ? undefined : gekozenVan;
  const to = omgedraaid ? undefined : gekozenTot;

  function wijzigPaginafilter(deel: FilterStand) {
    zetPaginafilter(PAGINAFILTER_SLEUTEL, { ...paginafilters, ...deel });
  }

  /**
   * Story 33.2 — of de volledige tabel opengeklapt is, uit het webadres.
   *
   * Dicht bij het laden, want de aandachtslijst erboven leidt het scherm; wie alle cijfers wil ziet
   * ze in één klik. Een onbekende waarde laat de tabel gewoon dicht, zonder foutmelding (AC-17).
   */
  const tabelOpen = leesTabelOpen(searchParams.get(TABEL_PARAMETER));

  /**
   * De persoon waarop de GRAFIEK gefilterd is, uit het webadres (AC-11).
   *
   * In het adres en niet in het geheugen van het scherm, zodat een gedeelde link hetzelfde toont —
   * dat is bij een rapportage het verschil tussen "kijk eens" en "kijk eens, en dan zelf zoeken".
   */
  // ALLEEN CIJFERS, net zo streng als de route erachter. `Number.parseInt` neemt `12abc` als 12 en
  // `-5` als -5; die laatste gaat de deur uit en komt terug als een fout, en dan leest een typefout
  // in een gedeelde link als een kapotte grafiek (reviewbevinding 27-08-2026).
  const persoonUitAdres = searchParams.get('persoon') ?? '';
  const gevraagdePersoon = /^\d+$/.test(persoonUitAdres) ? Number(persoonUitAdres) : null;

  /**
   * Het GLN (behandeltijd-rapport, CAP-3), uit het adres.
   *
   * Afwezig: het standaard-GLN. Leeg: alle GLN's, en dan gaat er geen GLN mee. Een ongeldige waarde
   * krijgt een melding bij het veld, en dan doet geen van de vier vragen een aanroep.
   */
  const glnUitAdres = searchParams.get('gln');
  const gekozenGln: string | undefined = glnUitAdres === null
    ? STANDAARD_GLN
    : glnUitAdres === '' ? undefined : glnUitAdres;
  const glnGeldig = gekozenGln === undefined || isGeldigGln(gekozenGln);

  const query = useHandlingTimePerPerson({ from, to }, { enabled: glnGeldig, gln: gekozenGln });
  const alleRijen = query.data?.data ?? [];

  /**
   * HET FILTER GAAT OP SLOT zodra de bron de uitsplitsing per stap niet meestuurt.
   *
   * Dat kan tijdens een uitrol gebeuren, met een serverlaag van vóór story 31.1. Filteren zou dan
   * iedereen wegstrepen en het scherm laten beweren dat er niets gemeten is, terwijl er alleen niets
   * uitgesplitst is. Het scherm toont dan alles, de knoppen zijn niet te bedienen, en er staat één
   * regel bij die zegt waarom.
   */
  const uitsplitsingMist = uitsplitsingOntbreekt(alleRijen);
  const filterOpSlot = uitsplitsingMist;
  const stapFilter: StapFilter = filterOpSlot ? 'beide' : gevraagdFilter;

  /**
   * Het werkvenster en het gat (behandeltijd-rapport, CAP-7). De pagina zelf is al alleen voor de rol
   * manager bereikbaar (`ManagerRoute` in router.tsx; de beheerder erft die rang). Deze vlag is de
   * tweede controle, op de plek waar de gegevens worden opgehaald: zonder de rol manager of beheerder
   * wordt de vraag niet gesteld, en dan staan er geen kolommen. Het stapfilter geldt hier ook.
   */
  // De EFFECTIEVE rol, zoals ProtectedRoute die ook gebruikt: tijdens een klantsimulatie is dat `customer`,
  // en dan wordt er niets opgehaald.
  const { role } = useEffectiveAccess();
  const heeftManagerRol = role === 'manager' || role === 'admin';
  // Het werkvenster staat alleen in de tabel, en de tabel is standaard dicht: dan hoeft er niets opgehaald.
  const werkvensterLezing = useHandlingTimeWorkWindow(
    { from, to },
    { enabled: heeftManagerRol && tabelOpen, step: stapFilter === 'beide' ? 'both' : stapFilter },
  );
  const werkvenster: WerkvensterWeergave | undefined = !heeftManagerRol
    ? undefined
    : werkvensterLezing.isError
      ? { status: 'fout' }
      : werkvensterLezing.isLoading
        ? { status: 'laden' }
        : { status: 'ok', perPersoon: samenvatPerPersoon(werkvensterLezing.data?.data ?? []) };

  /**
   * De regels zoals het scherm ze toont, en wie erdoor buiten beeld valt.
   *
   * ÉÉN snede voor het hele scherm, en met opzet hier: de kaarten, de tabel en de grafiek rekenen
   * allemaal verder op deze uitkomst. Zou elk onderdeel zelf filteren, dan is elke plek die het
   * vergeet een getal dat over twee stappen gaat terwijl de kop er één belooft (AC-2).
   */
  const gefilterd = beperkRijenTotStap(alleRijen, stapFilter);
  const rows = gefilterd.getoond;

  /**
   * De gevraagde persoon, MAAR ALLEEN ALS HIJ AANKLIKBAAR IS.
   *
   * Reviewbevinding van 27 augustus 2026, en het was een echt gat: de muis kon "Persoon verwijderd"
   * niet kiezen, maar `?persoon=2147483647` in de adresbalk zette het filter alsnog. De grafiek
   * toonde dan de reeks van een ONBEKEND aantal mensen met één naam erboven, alsof het één collega
   * was — precies wat epic 31 §2 verbiedt, alleen langs het toetsenbord.
   *
   * Dat dit pas kan zodra de regels binnen zijn, is geen omweg maar de aard van de vraag: of een
   * nummer bij een mens hoort weet je niet voordat je het antwoord hebt. Tot dat moment is er geen
   * filter, en dus ook geen vierde aanroep.
   */
  const persoon = gevraagdePersoon !== null
    && bouwTabelRegels(rows).some((regel) => regel.userId === gevraagdePersoon && regel.aanklikbaar)
    ? gevraagdePersoon
    : null;

  /**
   * De TWEEDE aanroep, over het even lange venster dat direct vóór de gekozen periode ligt.
   *
   * Besluit O-2 van 27 augustus 2026: twee vragen per schermweergave is aanvaard, want een absoluut
   * getal zonder richting is geen stuurinformatie — dat het deze maand 876 uur was zegt pas iets
   * naast de maand ervóór.
   *
   * Deze vraag mag het scherm NIET ophouden, en dat is hier geen belofte maar een eigenschap van de
   * opbouw: nergens hieronder wordt naar `vergelijking.isLoading` of `vergelijking.isError`
   * gekeken. Komt het antwoord niet, of komt het te laat, dan staan de cijfers er gewoon en zeggen
   * de kaarten dat er geen vergelijking is. Geen foutmelding, geen leeg scherm, geen molentje dat
   * blijft draaien.
   */
  const vorige = vorigePeriode({ from, to });
  const vergelijking = useHandlingTimePerPerson(
    { from: vorige?.from, to: vorige?.to },
    { enabled: !query.isError && glnGeldig, gln: gekozenGln },
  );

  /**
   * De DERDE aanroep: dezelfde periode, maar mét een tijdvak, zodat de bron er de reeks over de
   * tijd bij levert (story 31.3, AC-4). Eén vraag voor de hele reeks — nooit één per tijdvak.
   *
   * Waarom dit een eigen vraag is en niet het tijdvak op de eerste: omdat de grafiek dán mag
   * mislukken zonder de kerncijfers en de tabel mee te trekken (AC-11). Zou de reeks aan de eerste
   * vraag hangen, dan was elke storing in de reeks meteen een leeg scherm. Net als de vergelijking
   * wordt hij niet gesteld zodra de eerste vraag al mislukt is: dan is er toch niets te tekenen.
   *
   * De korrel kiest zichzelf naar de lengte van de periode — tot en met 31 dagen per dag, tot en
   * met een half jaar per week, daarboven per maand.
   */
  const korrel = from && to ? korrelVoor({ from, to }) : undefined;
  const reeks = useHandlingTimePerPerson(
    { from, to },
    { enabled: !query.isError && glnGeldig, bucket: korrel, gln: gekozenGln },
  );

  /**
   * De VIERDE aanroep: dezelfde periode en hetzelfde tijdvak, maar dan de reeks van één persoon
   * (story 31.4).
   *
   * Hij staat er ALLEEN wanneer er werkelijk gefilterd is, en dat verschil staat hieronder in de
   * code in plaats van in een toelichting: `enabled` is `false` zolang er niemand gekozen is, en
   * dan wordt er niets opgehaald. Het is het verschil tussen "soms vier vragen" en "altijd vier".
   *
   * En hij vervangt de derde vraag niet: de ongefilterde reeks blijft staan, zodat het loslaten van
   * het filter meteen weer de grafiek van iedereen toont zonder opnieuw te hoeven wachten.
   */
  const persoonsReeks = useHandlingTimePerPerson(
    { from, to },
    {
      enabled: persoon !== null && !query.isError && glnGeldig,
      bucket: korrel,
      gln: gekozenGln,
      ...(persoon !== null ? { userId: persoon } : {}),
    },
  );

  /**
   * De twee normen (story 31.5). Ze hangen niet aan de periode en niet aan een persoon: ze gelden
   * portaalbreed, dus ze worden één keer opgehaald en één keer gezet.
   */
  const normenQuery = useHandlingTimeNormen();
  const zetNormen = useZetHandlingTimeNormen();
  // De laatst BEWAARDE normen gaan vóór de opgehaalde: na een geslaagde wijziging rekent het scherm
  // meteen met de nieuwe norm in plaats van met de norm van vóór de klik.
  const bewaardeNormen: Normen = zetNormen.data ?? normenQuery.data ?? { vastleggen: null, valideren: null };

  /**
   * Wat er in de velden staat zolang er getypt wordt, of `null` zolang niemand iets aanraakte.
   *
   * Dat onderscheid is er met opzet: zonder eigen stand volgen de velden de bewaarde norm, en
   * daardoor staan ze meteen goed zodra het antwoord binnenkomt in plaats van leeg te blijven tot
   * iemand het scherm opnieuw opent.
   */
  // Wat er in de keuzelijst is aangeklikt. Nodig omdat twee keuzes op sommige dagen dezelfde periode
  // dekken; zonder dit springt de lijst dan terug naar de standaardkeuze.
  const [gekozenPreset, setGekozenPreset] = useState<PeriodePreset | null>(null);
  const [normVelden, setNormVelden] = useState<Normvelden | null>(null);
  const [normFout, setNormFout] = useState(false);
  const velden: Normvelden = normVelden ?? {
    vastleggen: alsVeld(bewaardeNormen.vastleggen),
    valideren: alsVeld(bewaardeNormen.valideren),
  };

  /**
   * Wat de grafiek en de tabel te zien krijgen.
   *
   * AFWEZIG zolang er geen enkele norm gezet is, en dat is meer dan een formaliteit: dan tonen de
   * grafiek en de tabel zich letterlijk zoals ze zich vóór story 31.5 toonden — geen lijn, geen
   * kleur en ook geen merktekens per stap (AC-7). De aandachtslijst krijgt met opzet de BEWAARDE
   * normen en niet deze: zonder norm hoort dáár juist te staan dat er nog geen is (story 33.2, AC-7).
   */
  const getoondeNormen: Normen | undefined =
    bewaardeNormen.vastleggen === null && bewaardeNormen.valideren === null ? undefined : bewaardeNormen;

  /**
   * De zin die de SERVERLAAG schreef bij een weigering, en niet die van de netwerklaag.
   *
   * `error.message` van een mislukte aanroep is "Request failed with status code 400" — Engels, en
   * het zegt een lezer niets. De nette Nederlandse zin staat in het antwoord zelf; `apiFoutmelding`
   * haalt hem daaruit, met een vertaalde terugval als hij ontbreekt (AC-9, codereviewbevinding
   * 29 augustus 2026).
   */
  const foutbron = apiFoutmelding(zetNormen.error);
  const weigering = foutbron.serverMessage ?? t(foutbron.messageKey);

  function zetVeld(stap: keyof Normvelden, waarde: string) {
    setNormFout(false);
    setNormVelden({ ...velden, [stap]: waarde });
  }

  function bewaarNormen() {
    const vastleggen = alsNorm(velden.vastleggen);
    const valideren = alsNorm(velden.valideren);
    if (!vastleggen.ok || !valideren.ok) {
      // Niets versturen dat toch geweigerd wordt, en vooral: geen `NaN` die als leeg aankomt en dan
      // stilzwijgend een norm zou wissen.
      setNormFout(true);
      return;
    }
    setNormFout(false);
    zetNormen.mutate({ vastleggen: vastleggen.norm, valideren: valideren.norm });
  }

  /**
   * Het GLN in het adres zetten, zonder een nieuwe pagina: `replace`, zodat elke toetsaanslag geen
   * terugknop-stap wordt. De rest van het adres blijft staan.
   */
  function zetGln(waarde: string) {
    const next = new URLSearchParams(searchParams);
    next.set('gln', waarde);
    setSearchParams(next, { replace: true });
  }

  /** De periode uit de centrale datumvelden; een leeg bereik blijft leeg (geen standaard eroverheen). */
  function zetPeriode(bereik: Datumbereik) {
    wijzigPaginafilter({ periode: bereik });
  }

  /**
   * Zet of laat het filter op één persoon los, en houdt de rest van het adres intact.
   *
   * De periode blijft staan: filteren op een persoon is een andere vraag dan een andere periode
   * kiezen, en de twee mogen elkaar niet wissen.
   */
  function kiesPersoon(userId: number | null) {
    const next = new URLSearchParams(searchParams);
    if (userId === null) next.delete('persoon');
    else next.set('persoon', String(userId));
    setSearchParams(next);
  }

  /**
   * Een vaste periode kiezen, en NIETS ANDERS aanraken: de gekozen stap en persoon blijven staan
   * (story 33.1, AC-15).
   */
  function kiesPreset(waarde: string) {
    if (waarde === '') return;
    setGekozenPreset(waarde as PeriodePreset);
    const periode = periodeVoor(waarde as PeriodePreset, new Date());
    wijzigPaginafilter({ periode: { van: periode.from, tot: periode.to } });
  }

  /** De stap kiezen, en de periode en de persoon laten staan (AC-14). */
  function kiesStap(volgende: StapFilter) {
    wijzigPaginafilter({ stap: [volgende] });
  }

  /** "Filters en sortering herstellen" bij een geweigerde link: periode, stap en tabel op de standaard. */
  function herstelStandaard() {
    wisGeweigerd();
    zetPaginafilter(PAGINAFILTER_SLEUTEL, {});
    useLijstStandStore.getState().zet(BEHANDELTIJD_TABEL_SLEUTEL, { filters: {}, sortering: [], pagina: 1 });
    // Het GLN gaat terug naar de standaard: de parameter gaat uit het adres. Functioneel, zodat de
    // laatste stand van het adres gebruikt wordt en niet de stand van het moment van renderen.
    setSearchParams((vorige) => {
      const next = new URLSearchParams(vorige);
      next.delete('gln');
      return next;
    }, { replace: true });
  }

  /**
   * De volledige tabel open- of dichtklappen (story 33.2, AC-17).
   *
   * De rest van het adres blijft staan: de tabel openen is een andere weergave van hetzelfde
   * antwoord, geen nieuwe vraag. Dicht is de beginstand, en dan staat de parameter er niet — zo
   * opent een gedeelde link zonder deze parameter zoals het scherm bedoeld is.
   */
  function zetTabelOpen(open: boolean) {
    const next = new URLSearchParams(searchParams);
    if (open) next.set(TABEL_PARAMETER, TABEL_OPEN);
    else next.delete(TABEL_PARAMETER);
    setSearchParams(next);
  }

  // Wie zelf datums invult valt uit de vaste keuzes; de lijst zegt dat dan ook. De eigen keuze gaat
  // mee als voorkeur, want op sommige dagen dekken twee keuzes precies dezelfde periode — dan hoort
  // de lijst te blijven staan op wat er is aangeklikt.
  const huidigePreset = from && to ? presetVoor({ from, to }, new Date(), gekozenPreset ?? STANDAARD_PRESET) : null;

  const hasPeriod = Boolean(from) && Boolean(to);

  /**
   * Of het filter te bedienen is.
   *
   * NIET zolang er helemaal geen cijfers op het scherm staan: zonder periode, en bij een dichte
   * poort of een storing op de eerste vraag. Klikken zou dan alleen het webadres veranderen, met
   * drie knoppen die netjes oplichten en een scherm dat niet beweegt — precies het "er gebeurt iets,
   * dus het werkte"-patroon dat deze story juist opruimt (codereviewbevinding 30 augustus 2026).
   *
   * En niet zolang de uitsplitsing per stap ontbreekt; dáár staat de uitleg bij.
   */
  const filterBedienbaar = hasPeriod && !filterOpSlot && !query.isError && glnGeldig;

  /**
   * De voorlopig-melding (behandeltijd-rapport, CAP-5). De minuten van de tot-datum kunnen nog veranderen
   * tot 02:00 Amsterdamse tijd op de dag erna; daarna verdwijnt de melding. Zie `voorlopigZichtbaar`.
   */
  // Het tijdstip loopt mee: een open scherm moet de melding precies om 02:00 laten verdwijnen, niet pas bij
  // de volgende klik. Eén timer op het grenstijdstip zelf, opnieuw gezet als de tot-datum verandert.
  const [nu, zetNu] = useState(() => new Date());
  useEffect(() => {
    if (to === undefined) return;
    const grens = voorlopigTot(to).getTime();
    let id: number | undefined;
    // Een setTimeout telt niet verder dan 2^31-1 ms (ruim 24 dagen): een verder weg tijdstip wordt in
    // stappen gewacht, en elke stap zet `nu` opnieuw zodat de melding op het juiste moment verdwijnt.
    const wacht = () => {
      zetNu(new Date());
      const rest = grens - Date.now();
      if (rest > 0) id = window.setTimeout(wacht, Math.min(rest, 2_147_483_647));
    };
    wacht();
    return () => window.clearTimeout(id);
  }, [to]);
  const voorlopig = hasPeriod && to !== undefined && voorlopigZichtbaar(to, nu);

  // De kaarten worden HIER opgeteld en niet bij de bron opgehaald, om dezelfde reden waarom het
  // gemiddelde per stuk in de tabel hier gedeeld wordt: een lezer moet kunnen narekenen wat hij
  // ziet, en de kaarten en de tabel kunnen zo niet uit elkaar lopen.
  const kerncijfers = berekenKerncijfers(rows);

  /**
   * Dezelfde snede op de vorige periode, anders vergelijkt een kaart één stap met twee.
   *
   * MAAR NIET wanneer juist DÁT antwoord de uitsplitsing mist. Dan valt er over de vorige periode
   * per stap niets te zeggen, en filteren zou er nul van maken: elke kaart meldde dan "in de dertig
   * dagen ervoor is niets gemeten" terwijl er alleen niets uitgesplitst is. Er is dan geen
   * vergelijking, en dat is wat de kaarten horen te zeggen (codereviewbevinding 30 augustus 2026).
   */
  const vergelijkingBruikbaar = vergelijking.data !== undefined
    && (stapFilter === 'beide' || !uitsplitsingOntbreekt(vergelijking.data.data));
  const kerncijfersVorigePeriode = vergelijkingBruikbaar && vergelijking.data
    ? berekenKerncijfers(beperkRijenTotStap(vergelijking.data.data, stapFilter).getoond)
    : null;

  /**
   * Story 33.3, kaart 2 — hoe de gemeten tijd over de mensen ligt.
   *
   * Op dezelfde regels als de kaart erboven, en dus op de hele periode: het filter op één persoon
   * verandert de grafiek en niets anders (story 31.4, AC-10).
   */
  const persoonsverdeling = bouwPersoonsverdeling(rows);

  // De grafiek staat ONDER de aandachtslijst en BOVEN de tabel, in allebei de takken hieronder: ook
  // een periode zonder één regel houdt zijn grafiek, en die zegt dan zelf dat er niets te tonen is.
  // Met een filter aan tekent de grafiek de reeks van DÍE persoon; zonder filter die van iedereen.
  // De kaarten en de tabel blijven in beide gevallen op de eerste vraag staan, en dus op de hele
  // periode — anders verandert het filter stilzwijgend de betekenis van elk getal op het scherm.
  const getoondeReeks = persoon === null ? reeks : persoonsReeks;
  // Dezelfde snede op de tijdvakken: een staaf van een stap die het filter wegliet mag er niet zijn.
  const getoondeBuckets = beperkBucketsTotStap(getoondeReeks.data?.meta.buckets, stapFilter);
  /**
   * Story 33.3, kaart 3 — waar het getal op rust.
   *
   * Uit de reeks die het scherm AL ophaalt, en niet uit een extra aanroep (AC-12). Nadrukkelijk uit
   * de ONGEFILTERDE reeks: de kaart hoort bij het totaal erboven, en dat totaal gaat over iedereen,
   * ook wanneer de grafiek op één persoon staat.
   *
   * `null` zolang die reeks er niet is — dan is de dekking NIET TE BEPALEN, en dat is iets anders
   * dan "nul van de dertig", want dat laatste leest als "er is niets gemeten" (AC-13).
   */
  const dekkingsBuckets = reeks.data?.meta.buckets;
  const dekking = from && to && korrel !== undefined && dekkingsBuckets !== undefined
    ? bouwDekking(bouwReeks({ from, to }, beperkBucketsTotStap(dekkingsBuckets, stapFilter), korrel))
    : null;

  const gefilterdeRegel = persoon === null ? undefined : rows.find((row) => row.userId === persoon);
  const gefilterdOp = gefilterdeRegel === undefined ? (persoon === null ? null : String(persoon)) : naamVoorRegel(gefilterdeRegel, t);

  /**
   * DE TWEE NORMEN (story 31.5), als één onderdeel.
   *
   * Eén keer opgeschreven en op twee plekken getoond — in de lege tak en in de gewone — want anders
   * moet elke wijziging tweemaal en gedraagt het scherm zich anders zodra er geen enkele regel is
   * (codereviewbevinding 29 augustus 2026).
   *
   * Ze staan op het scherm zelf en niet in het beheerscherm: dat staat achter `admin` en eist een
   * reden bij elke wijziging, en dat sluit precies de leidinggevende buiten die de norm hoort te
   * zetten. Er wordt hier dan ook GEEN reden gevraagd — wie de norm zette en wanneer vult de
   * serverlaag zelf.
   *
   * Een leeg veld is GEEN norm en nadrukkelijk geen nul: de drie plekken eronder tonen zich dan
   * zoals ze zich vóór deze story toonden.
   */
  const normenBlok = (
    /* GEEN KOP MAAR EEN BENOEMD GEBIED, en dat is een bevinding uit de epic-brede review van
       30 augustus 2026. Dit blok stond met een `h2` bovenaan het scherm en LEIDDE daarmee de
       koppenstructuur, terwijl de aandachtslijst het scherm hoort te leiden — een schermlezer kreeg
       dus een andere leesvolgorde te horen dan het scherm bedoelt. Het is bovendien geen
       inhoudssectie maar een invulblok bij de bediening bovenaan. Met `aria-labelledby` blijft het
       een gebied met de naam "Normen", dus met de gebiedsnavigatie even goed te vinden als eerst. */
    <section
      aria-labelledby="handling-time-normen-titel"
      className="rounded-[var(--radius-card)] border border-[var(--border-soft)] bg-[var(--background-muted)] px-4 py-3"
      data-testid="handling-time-normen"
    >
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-0 flex-1 sm:min-w-[16rem]">
          <p className="text-sm font-semibold text-[var(--foreground)]" id="handling-time-normen-titel">{t('handlingTime.normen.titel')}</p>
          <p className="mt-1 max-w-[46rem] text-xs leading-snug text-[var(--foreground-muted)]">
            {t('handlingTime.normen.uitleg')}
          </p>
        </div>
        {NORM_STAPPEN.map((stap) => (
          <label className="flex flex-col gap-1 text-xs text-[var(--foreground-muted)]" key={stap}>
            {t(`handlingTime.normen.${stap}`)}
            {/* MET OPZET GEEN veld van het soort `number`, en dat is een codereviewbevinding van
                29 augustus 2026. Zo'n veld maakt volgens de HTML-norm van élke invoer die het niet
                begrijpt een LEGE waarde — "4,5" dus ook. Een leeg veld betekent hier "geen norm",
                en Opslaan gooide daarmee de bestaande norm weg zonder één melding: wie "4,5" typte
                wiste zijn eigen norm.
                Als tekst komt binnen wat er getypt is: de komma wordt aanvaard, en alles wat daarna
                nog geen getal boven nul is wordt zichtbaar geweigerd in plaats van stil gelezen als
                "geen norm". `min` en `step` staan er niet meer bij — die suggereerden bovendien dat
                nul mocht, terwijl de serverlaag hem afkeurt. */}
            <input
              aria-label={t(`handlingTime.normen.${stap}`)}
              className="h-9 w-36 rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] px-3 text-sm tabular-nums"
              data-testid={`handling-time-norm-${stap}`}
              inputMode="decimal"
              onChange={(event) => zetVeld(stap, event.target.value)}
              type="text"
              value={velden[stap]}
            />
          </label>
        ))}
        <button
          className="h-9 rounded-[var(--radius-control)] bg-[var(--primary)] px-4 text-sm font-semibold text-white disabled:opacity-60"
          data-testid="handling-time-normen-opslaan"
          disabled={zetNormen.isPending}
          onClick={bewaarNormen}
          type="button"
        >
          {t(`handlingTime.normen.${zetNormen.isPending ? 'opslaanBezig' : 'opslaan'}`)}
        </button>
      </div>

      {/* Een geweigerde norm wordt HIER gemeld, bij de velden waar de invoer staat, en trekt de
          rest van het scherm niet mee: de cijfers eronder kloppen nog steeds (AC-9). */}
      {(normFout || zetNormen.isError) && (
        <p className="mt-2 text-xs text-[var(--foreground)]" data-testid="handling-time-normen-fout">
          {normFout ? t('handlingTime.normen.foutOnbekend') : t('handlingTime.normen.fout', { melding: weigering })}
        </p>
      )}
    </section>
  );

  /**
   * HET STAPFILTER (story 33.1): één bediening met drie standen, die op ALLES doorwerkt.
   *
   * Hij staat naast de periodekeuze en niet bij de tabel, en dat is geen opmaakkeuze: hij bepaalt
   * waar élk getal op dit scherm over gaat — de kaarten, de grafiek én de tabel. Een filter dat
   * boven de tabel hangt belooft dat hij alleen de tabel raakt, en dat was precies de verwarring
   * die deze story opruimt.
   *
   * Knoppen met een aan/uit-stand en geen keuzelijst: de drie standen staan alle drie zichtbaar
   * naast elkaar, ze zijn met de tab-toets te bereiken, en `aria-pressed` zegt een schermlezer
   * welke er aanstaat (AC-17).
   */
  const stapFilterBlok = (
    <div className="flex flex-col gap-1 text-xs text-[var(--foreground-muted)]">
      {t('handlingTime.stapfilter.label')}
      <div
        aria-label={t('handlingTime.stapfilter.toelichting')}
        className="flex h-9 items-center gap-1.5"
        data-testid="handling-time-stapfilter"
        role="group"
      >
        {STAPFILTERS.map((stand) => (
          <button
            aria-pressed={stapFilter === stand}
            className={`h-9 rounded-[var(--radius-control)] border px-3 text-sm font-medium disabled:opacity-60 ${
              stapFilter === stand
                ? 'border-[var(--primary)] bg-[var(--primary-faint)] text-[var(--foreground-heading)]'
                : 'border-[var(--border)] bg-[var(--background)] text-[var(--foreground-muted)]'
            }`}
            data-testid={`handling-time-stapfilter-${stand}`}
            disabled={!filterBedienbaar}
            key={stand}
            onClick={() => kiesStap(stand)}
            type="button"
          >
            {t(`handlingTime.stapfilter.${stand}`)}
          </button>
        ))}
      </div>
    </div>
  );

  /**
   * WIE ER DOOR HET FILTER BUITEN BEELD VALT, bij naam, in één regel onder de tabel.
   *
   * Dit is de tegenhanger van het filter en geen extraatje: weglaten is eerlijker dan doorstrepen,
   * maar alleen zolang het scherm zegt wie het weglaat. Zonder deze regel verdwijnt een collega
   * geruisloos uit een overzicht waarop gestuurd wordt (AC-6).
   *
   * Hij staat er ALTIJD wanneer er iemand wegvalt — niet achter een knopje, en ook wanneer er
   * niemand overblijft en er dus geen tabel meer staat om hem onder te zetten.
   */
  const weggevallenRegel = gefilterd.weggevallen.length === 0 ? null : (
    <p className="text-xs leading-relaxed text-[var(--foreground-muted)]" data-testid="handling-time-weggevallen">
      {t('handlingTime.weggevallen', {
        count: gefilterd.weggevallen.length,
        namen: gefilterd.weggevallen.map((rij) => naamVoorRegel(rij, t)).join(', '),
        stap: t(`handlingTime.stapfilter.${stapFilter}`).toLowerCase(),
      })}
    </p>
  );

  /**
   * DE AANDACHTSLIJST (story 33.2): waar zit de tijd boven de norm, en wat kost dat.
   *
   * Hij staat BOVEN de tabel en rekent binnen de stap die het filter gekozen heeft; op "Beide"
   * levert dat twee lijsten onder elkaar op, elk tegen hun eigen norm (AC-1 en AC-5).
   *
   * Hij krijgt `bewaardeNormen` en niet `getoondeNormen`, en dat verschil is precies de story:
   * `getoondeNormen` is AFWEZIG zodra er geen enkele norm gezet is, en dan zouden de kaarten en de
   * tabel zich zonder norm tonen — wat klopt — maar zou dit blok helemaal verdwijnen. Juist dán
   * hoort er te staan dat er nog geen norm is en waar die gezet wordt (AC-7).
   */
  const aandachtsStappen: readonly StapKeuze[] = stapFilter === 'beide' ? ['vastleggen', 'valideren'] : [stapFilter];
  const aandachtslijst = (
    <Aandachtslijst
      gekozenPersoon={persoon}
      normen={bewaardeNormen}
      onKiesPersoon={kiesPersoon}
      rijen={rows}
      stappen={aandachtsStappen}
      uitsplitsingMist={uitsplitsingMist}
    />
  );

  /**
   * HET BLOK PER SOORT PRODUCT (story 34.1): waar ging de tijd heen.
   *
   * Het staat ONDER de aandachtslijst en volgt dezelfde stap: op "Beide" twee blokken onder elkaar,
   * elk met hun eigen elf regels, en nooit een blok waarin de twee stappen zijn opgeteld (AC-11).
   *
   * De blokken komen ONBEWERKT uit de bron en worden hier niet op de stap gesneden zoals de regels
   * per persoon dat wel worden: `beperkRijenTotStap` snijdt in de mensen, en dit blok gaat over
   * producten. `bouwCategorieBlokken` kiest zelf de stap die gevraagd is.
   */
  const categorieBlok = (
    <CategorieBlok blokken={query.data?.meta?.productCategories} stappen={aandachtsStappen} />
  );

  /**
   * DE VOLLEDIGE TABEL, sinds story 33.2 ingeklapt achter één opening (AC-17).
   *
   * Wat epic 31 opleverde blijft ONVERANDERD staan zodra hij open is: de positiekolom, de
   * eerlijkheidsnoot, de sorteerbare kolommen (AC-16). Alleen de plek verandert — hij staat onder
   * de aandachtslijst, en niet meer als eerste wat een lezer ziet.
   *
   * De inhoud wordt pas neergezet als hij open is, en niet met een verborgen stijl weggemoffeld:
   * een ingeklapte tabel die er toch staat is voor een schermlezer en voor het toetsenbord wél
   * aanwezig, en dat is precies het "er staat iets wat er niet lijkt te zijn" dat dit epic opruimt.
   */
  const tabelBlok = (
    <div className="space-y-2">
      {/* `aria-controls` ALLEEN WANNEER HET DOEL BESTAAT: de inhoud wordt pas neergezet als de
          tabel open is, en een verwijzing naar een id dat er niet is, is een kapotte verwijzing
          (codereviewbevinding 30 augustus 2026). */}
      <button
        {...(tabelOpen ? { 'aria-controls': TABEL_INHOUD_ID } : {})}
        aria-expanded={tabelOpen}
        className="flex w-full items-center gap-2 rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] px-4 py-3 text-sm font-semibold text-[var(--foreground)]"
        data-testid="handling-time-tabel-opening"
        onClick={() => zetTabelOpen(!tabelOpen)}
        type="button"
      >
        <span aria-hidden="true" className={`mdi ${tabelOpen ? 'mdi-chevron-down' : 'mdi-chevron-right'} text-base text-[var(--foreground-muted)]`} />
        {t('handlingTime.table.opening')}
      </button>
      {tabelOpen && (
        <div id={TABEL_INHOUD_ID}>
          <BehandeltijdTabel
            gekozenPersoon={persoon}
            normen={getoondeNormen}
            onKiesPersoon={kiesPersoon}
            rijen={rows}
            stapFilter={stapFilter}
            werkvenster={werkvenster}
            {...(start[BEHANDELTIJD_TABEL_SLEUTEL] ? { start: start[BEHANDELTIJD_TABEL_SLEUTEL] } : {})}
          />
        </div>
      )}
    </div>
  );

  /**
   * DE DRIE CONTEXTKAARTEN (story 33.3), ONDERAAN.
   *
   * Ze stonden tot deze story als vier kerncijfers bovenaan. Ze beantwoorden nu een andere vraag —
   * hoe hard is dit getal en waar komt het vandaan — en ze staan onder de aandachtslijst, de
   * grafiek en de ingeklapte tabel. Vier grote getallen bovenaan trokken de eerste blik weg van wat
   * er te sturen valt, en dat is precies wat epic 33 opruimt.
   *
   * Eén keer opgeschreven en op twee plekken getoond — in de lege tak en in de gewone — om dezelfde
   * reden als bij het normenblok: anders moet elke wijziging tweemaal.
   */
  const contextkaarten = (
    <Contextkaarten
      cijfers={kerncijfers}
      dagen={vorige?.dagen ?? 0}
      dekking={dekking}
      dekkingBezig={reeks.isLoading}
      {...(stapFilter === 'beide' ? {} : { stap: stapFilter })}
      verdeling={persoonsverdeling}
      vorige={kerncijfersVorigePeriode}
    />
  );

  const grafiek = hasPeriod && from && to
    ? (
      <BehandeltijdGrafiek
        buckets={getoondeBuckets}
        error={getoondeReeks.error}
        gefilterdOp={gefilterdOp}
        isError={getoondeReeks.isError}
        isLoading={getoondeReeks.isLoading}
        normen={getoondeNormen}
        onFilterLos={() => kiesPersoon(null)}
        onRetry={() => getoondeReeks.refetch()}
        periode={{ from, to }}
        stapFilter={stapFilter}
      />
    )
    : null;

  // Per etiket (vervolg behandeltijd-rapportage, CAP-4): onder het overzicht, maar vóór de drie
  // kaarten, die het scherm afsluiten (epic 33, story 33.3 AC-14). Alleen met een geldige periode en partij.
  // Dezelfde rolgrens als het werkvenster: het per-etiket-antwoord toont namen en minuten per etiket.
  const perEtiketBlok = <PerEtiketOverzicht enabled={hasPeriod && glnGeldig && heeftManagerRol} from={from} gln={gekozenGln} to={to} />;

  return (
    <div className="space-y-6" data-testid="handling-time-page">
      <header>
        <h1 className="text-xl font-bold tracking-tight text-[var(--foreground)]" ref={kopRef} tabIndex={-1}>{t('handlingTime.title')}</h1>
        <p className="mt-1 text-sm text-[var(--foreground-muted)]">{t('handlingTime.subtitle')}</p>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="flex flex-col gap-1 text-xs text-[var(--foreground-muted)]">
            {t('handlingTime.gln.label')}
            <input
              className={`h-9 w-40 rounded-[var(--radius-control)] border bg-[var(--background)] px-3 text-sm tabular-nums ${
                glnGeldig ? 'border-[var(--border)]' : 'border-[var(--foreground)]'
              }`}
              aria-describedby={glnGeldig ? undefined : 'handling-time-gln-fout'}
              aria-invalid={!glnGeldig}
              data-testid="handling-time-gln"
              inputMode="numeric"
              onChange={(event) => zetGln(event.target.value)}
              type="text"
              value={glnUitAdres ?? STANDAARD_GLN}
            />
          </label>
          {!glnGeldig && (
            <p className="text-xs text-[var(--foreground)]" data-testid="handling-time-gln-fout" id="handling-time-gln-fout">
              {t('handlingTime.gln.fout')}
            </p>
          )}
        </div>
        <label className="flex flex-col gap-1 text-xs text-[var(--foreground-muted)]">
          {t('handlingTime.periode')}
          <select
            aria-label={t('handlingTime.periode')}
            className="h-9 rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
            data-testid="handling-time-preset"
            onChange={(event) => kiesPreset(event.target.value)}
            value={huidigePreset ?? ''}
          >
            {huidigePreset === null && <option value="">{t('handlingTime.preset.aangepast')}</option>}
            {PRESETS.map((preset) => (
              <option key={preset} value={preset}>{t(`handlingTime.preset.${preset}`)}</option>
            ))}
          </select>
        </label>
        {/* De centrale datumvelden "Vanaf" en "Tot en met" (spec-tabelfilters CAP-3, CAP-13). */}
        <DatumFilter bereik={gekozenBereik} kolom={PERIODE_KOLOM} onChange={zetPeriode} />
        {stapFilterBlok}
      </div>

      <FilterRejectedMessage baseTestId={BASIS_TESTID} focusNaWissen={kopRef} linkGeweigerd={geweigerd} onClear={herstelStandaard} />

      {/* De bronregel (behandeltijd-rapport, CAP-5): hoe de tijd gemeten en gerekend is. Geen klokuur,
          want die toont het scherm zelf niet. */}
      <p className="text-xs leading-relaxed text-[var(--foreground-muted)]" data-testid="handling-time-bronregel">
        {t('handlingTime.bronregel')}
      </p>

      {voorlopig && !query.isError && (
        <p className="text-xs leading-relaxed text-[var(--foreground)]" data-testid="handling-time-voorlopig">
          {t('handlingTime.voorlopig')}
        </p>
      )}

      {/* Waaróm het filter niet te bedienen is, en niet stilzwijgend een grijze knop. */}
      {filterOpSlot && (
        <p className="text-xs text-[var(--foreground-muted)]" data-testid="handling-time-stapfilter-uitleg">
          {t('handlingTime.stapfilter.geenUitsplitsing')}
        </p>
      )}

      {!hasPeriod ? (
        <p className="text-sm text-[var(--foreground-muted)]" data-testid="handling-time-no-period">
          {t('handlingTime.choosePeriod')}
        </p>
      ) : !glnGeldig ? (
        // Een ongeldig GLN heeft geen cijfers, en "geen metingen" zou dan een leugen zijn. De melding
        // staat bij het veld; hier blijft het leeg.
        null
      ) : isHandlingTimeGateClosed(query.error) ? (
        // Dicht is geen storing. Geen rood, en geen knop "opnieuw proberen": die kan niets
        // veranderen aan een instelling die ontbreekt, en hij belooft dus iets wat hij niet waarmaakt.
        <p className="text-sm text-[var(--foreground-muted)]" data-testid="handling-time-gate-closed">
          {t('handlingTime.gateClosed')}
        </p>
      ) : query.isError ? (
        // Een verkeerde periode of een onbereikbare dienst is hier GEEN leeg overzicht. Die komt
        // met een eigen melding binnen, en daar hoort de knop wél bij.
        <ApiErrorMessage error={query.error} onRetry={() => query.refetch()} />
      ) : query.isLoading ? (
        <div className="space-y-2" data-testid="handling-time-loading">
          {Array.from({ length: 3 }).map((_, index) => <div className="skeleton h-8 rounded-lg" key={index} />)}
        </div>
      ) : rows.length === 0 ? (
        // Leeg is een geldig antwoord en geen nul: hier staat GEEN regel met nul minuten.
        //
        // De KAARTEN blijven wél staan, en dat is een reviewbevinding van 27 augustus 2026. Ze
        // tonen dan streepjes — en juist dán is de vergelijking het meest de moeite waard: "in de
        // 30 dagen ervoor is hier wél gewerkt" is stuurinformatie, en die verdween mee met het
        // kaartenblok toen dit een of-of was.
        <>
          {normenBlok}
          {grafiek}
          {/* TWEE VERSCHILLENDE LEGE SCHERMEN, en dat verschil is het hele punt (AC-8). "In deze
              periode is niets gemeten" is een uitspraak over de periode; "in deze stap is niets
              gemeten" is er een over de keuze die de lezer zojuist maakte, en die kan hij ongedaan
              maken. Ze door elkaar halen laat een filter als een storing lezen. */}
          {alleRijen.length === 0 ? (
            <p className="text-sm text-[var(--foreground-muted)]" data-testid="handling-time-empty">
              {t('handlingTime.empty')}
            </p>
          ) : (
            <p className="text-sm text-[var(--foreground-muted)]" data-testid="handling-time-stap-leeg">
              {t('handlingTime.stapLeeg', { stap: t(`handlingTime.stapfilter.${stapFilter}`).toLowerCase() })}
            </p>
          )}
          {weggevallenRegel}
          {perEtiketBlok}
          {contextkaarten}
        </>
      ) : (
        <>
          {normenBlok}
          {/* DE VOLGORDE IS DE STORY. De aandachtslijst leidt het scherm — daar valt op te sturen —
              dan het verloop, dan de volledige tabel achter zijn opening, en pas onderaan de drie
              kaarten die zeggen hoe hard de cijfers zijn (epic 33, story 33.3 AC-14). */}
          {aandachtslijst}
          {categorieBlok}
          {grafiek}
          {tabelBlok}
          {weggevallenRegel}
          {perEtiketBlok}
          {contextkaarten}
        </>
      )}
    </div>
  );
}
